import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Navbar from './utils/Navbr';
import Loader from './utils/Loader';
import './style/PublisherWiseReport.css';

// Helper for default dates
const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
};

const PublisherWiseReport = () => {
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [loader, setLoader] = useState(true);
    const [filter, setFilter] = useState({
        startDate: getTodayDate(),
        endDate: getTodayDate()
    });

    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({
        totalClicks: 0,
        totalConversions: 0,
        totalEarned: 0,
        totalRevenue: 0
    });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Logic ---
    const fetchReport = async () => {
        setLoading(true);
        const adminToken = getCookie('adminToken');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchPublisherWiseReport',
                adminToken: adminToken || 'demo_token',
                startDate: filter.startDate,
                endDate: filter.endDate
            });

            if (response.data.status === true) {
                processData(response.data.data);
            }
        } catch (error) {
            console.error("Fetch Error", error);
        } finally {
            setTimeout(() => setLoading(false), 500);
        }
    };

    const processData = (data) => {
        setReportData(data);
        const totals = data.reduce((acc, curr) => ({
            totalClicks: acc.totalClicks + parseInt(curr.raw_clicks || 0),
            totalConversions: acc.totalConversions + parseInt(curr.conversions || 0),
            totalEarned: acc.totalEarned + parseFloat(curr.earned || 0),
            totalRevenue: acc.totalRevenue + parseFloat(curr.revenue || 0)
        }), { totalClicks: 0, totalConversions: 0, totalEarned: 0, totalRevenue: 0 });
        setSummary(totals);
    };

    useEffect(() => {
        const adminToken = getCookie('adminToken');
         setLoader(true);
        const init = async () => {
            try {
                // ✅ 1. Admin Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin',
                    adminToken
                });

                if (!authRes.data.status) {
                    toast.error(authRes.data.message || 'Admin session expired');
                    navigate('/cbMediaAdmin7/login');
                    return;
                }

                fetchReport();

            } catch (err) {
                toast.error('Admin auth check failed');
                setLoader(false);
                navigate('/cbMediaAdmin7/login');
            } finally{
                setLoader(false);
            }
        };

        init();
    }, [filter.startDate, filter.endDate]);

    // Handle Input Change
    const handleDateChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    };

    // Programmatic Date Picker Trigger
    const triggerDatePicker = (id) => {
        const element = document.getElementById(id);
        if (element && element.showPicker) {
            element.showPicker();
        } else {
            element.focus();
        }
    };

    const handleViewDetail = (pubId) => {
        const dateRangeStr = `${filter.startDate}_${filter.endDate}`;
        navigate(`/cbMediaAdmin7/reports/performance/${pubId}/${dateRangeStr}`);
    };
    if (loader) return <Loader />;
    return (
        <div className="report-wrapper_pwr">
            <Navbar role="Administrator" isAdmin={true} />

            <div className="bg-shape_pwr shape-1_pwr"></div>
            <div className="bg-shape_pwr shape-2_pwr"></div>

            <div className="content-container_pwr">

                {/* Header & Filter */}
                <div className="report-header_pwr">
                    <div className="header-text_pwr">
                        <h2>Publisher Wise <span className="highlight_pwr">Performance</span></h2>
                        <p>Track individual publisher metrics & earnings</p>
                    </div>

                    <div className="filter-group_pwr">

                        {/* Custom Date 1 */}
                        <div className="custom-date-wrapper_pwr" onClick={() => triggerDatePicker('start-date-pwr')}>
                            <span className="date-label_pwr">FROM</span>
                            <div className="date-display_pwr">
                                <span>{filter.startDate}</span>
                                <div className="calendar-icon_pwr">📅</div>
                            </div>
                            <input
                                id="start-date-pwr"
                                type="date"
                                name="startDate"
                                value={filter.startDate}
                                onChange={handleDateChange}
                                className="hidden-date-input_pwr"
                            />
                        </div>

                        <div className="date-separator_pwr"></div>

                        {/* Custom Date 2 */}
                        <div className="custom-date-wrapper_pwr" onClick={() => triggerDatePicker('end-date-pwr')}>
                            <span className="date-label_pwr">TO</span>
                            <div className="date-display_pwr">
                                <span>{filter.endDate}</span>
                                <div className="calendar-icon_pwr">📅</div>
                            </div>
                            <input
                                id="end-date-pwr"
                                type="date"
                                name="endDate"
                                value={filter.endDate}
                                onChange={handleDateChange}
                                className="hidden-date-input_pwr"
                            />
                        </div>

                    </div>
                </div>

                {/* Summary Cards */}
                <div className="summary-row_pwr">
                    <div className="sum-card_pwr">
                        <span>Total Clicks</span>
                        <strong>{summary.totalClicks.toLocaleString()}</strong>
                    </div>
                    <div className="sum-card_pwr">
                        <span>Conversions</span>
                        <strong className="success-text_pwr">{summary.totalConversions.toLocaleString()}</strong>
                    </div>
                    <div className="sum-card_pwr">
                        <span>Pub. Earned</span>
                        <strong className="earn-text_pwr">₹{summary.totalEarned.toLocaleString()}</strong>
                    </div>
                    <div className="sum-card_pwr">
                        <span>Net Revenue</span>
                        <strong className="revenue-text_pwr">₹{summary.totalRevenue.toLocaleString()}</strong>
                    </div>
                </div>

                {/* Table Section */}
                <div className="table-card_pwr">
                    {loading ? (
                        <div className="table-loader_pwr">
                            <div className="spinner_pwr"></div>
                            <p>Loading Publisher Data...</p>
                        </div>
                    ) : (
                        <div className="table-responsive_pwr">
                            <table className="report-table_pwr">
                                <thead>
                                    <tr>
                                        <th>Pub ID</th>
                                        <th>Name</th>
                                        <th className="text-center_pwr">Unique Clicks</th>
                                        <th className="text-center_pwr">Raw Clicks</th>
                                        <th className="text-center_pwr">Conv.</th>
                                        <th className="text-right_pwr">Earned</th>
                                        <th className="text-right_pwr">Withdraw</th>
                                        <th className="text-right_pwr">Revenue</th>
                                        <th className="text-center_pwr">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportData.length > 0 ? (
                                        reportData.map((row, index) => (
                                            <tr key={index} className="table-row_pwr">
                                                <td className="id-col_pwr">{row.publisher_id}</td>
                                                <td className="name-col_pwr">{row.name}</td>
                                                <td className="text-center_pwr">{row.unique_clicks}</td>
                                                <td className="text-center_pwr muted_pwr">{row.raw_clicks}</td>
                                                <td className="text-center_pwr success-text_pwr font-bold_pwr">{row.conversions}</td>
                                                <td className="text-right_pwr earn-text_pwr font-bold_pwr">₹{row.earned.toLocaleString()}</td>
                                                <td className="text-right_pwr text-danger_pwr">₹{row.withdraw.toLocaleString()}</td>
                                                <td className="text-right_pwr font-bold_pwr">₹{row.revenue.toLocaleString()}</td>
                                                <td className="text-center_pwr">
                                                    <button
                                                        className="action-btn-black_pwr"
                                                        onClick={() => handleViewDetail(row.publisher_id)}
                                                    >
                                                        VIEW REPORT
                                                        <div className="wave_pwr"></div>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="9" className="no-data_pwr">No publisher data found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default PublisherWiseReport;